import React, { useState } from 'react';
import Btn from '../common/Btn';
import ConfirmDialog from '../branches/ConfirmDialog';
import { setAlert } from '../../helpers/alerts';
import { apiTriggerStep } from '../../api/releases';
import { deleteReleaseWithAlert, setReleaseTagWithAlert } from './releaseActions';

const ICO_BRANCH = 'M6 3v12 M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 9a9 9 0 0 1-9 9';
const ICO_PLAY   = 'm5 3 14 9-14 9V3z';
const ICO_TAG    = 'M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01';
const ICO_STAR   = 'm12 2 2.9 6.26L22 9.27l-5 4.87 1.18 6.86L12 17.77l-6.18 3.23L7 14.14 2 9.27l7.1-1.01L12 2z';
const ICO_TRASH  = 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6';
const ICO_XMARK  = 'M18 6 6 18M6 6l12 12';

const EMPTY_DIALOG = { open: false, title: '', message: '', danger: false, confirmLabel: '', onConfirm: () => {} };

function triggerStep(version, stage, { label, reloadData, messageFor }) {
  apiTriggerStep(version, { stage }).then((r) => {
    reloadData?.();
    setAlert(label, messageFor(r.release), 'success');
  }).catch((e) => setAlert(`${label} error`, e, 'danger'));
}

export default function ActionsForReleaseList({ release, reloadData }) {
  const [dialog, setDialog] = useState(EMPTY_DIALOG);
  const confirm = (opts) => setDialog({ open: true, ...opts });
  const closeDialog = () => setDialog(EMPTY_DIALOG);

  const handleDelete = () => confirm({
    title: 'Delete release',
    message: `Are you sure you want to delete release "${release.version}"? This can't be undone.`,
    danger: true,
    confirmLabel: 'Delete',
    onConfirm: () => {
      closeDialog();
      deleteReleaseWithAlert(release.version, { reloadData, successTitle: 'Delete release', errorTitle: 'Delete error' }).catch(() => {});
    },
  });

  const handleRelease = () => confirm({
    title: 'Release',
    message: `Are you sure you want to trigger release "${release.version}"?`,
    confirmLabel: 'Release',
    onConfirm: () => {
      closeDialog();
      triggerStep(release.version, 'releasing', { label: 'Release', reloadData, messageFor: (r) => `Releasing version "${r.version}"` });
    },
  });

  const handleBranch  = () => triggerStep(release.version, 'branching', { label: 'Create branches', reloadData, messageFor: (r) => `Creating branches for release "${r.version}"` });
  const handleRunTest = () => triggerStep(release.version, 'testing', { label: 'Run tests', reloadData, messageFor: (r) => `Running tests for release "${r.version}"` });
  const handleCancel  = () => triggerStep(release.version, 'cancelled', { label: 'Cancel', reloadData, messageFor: (r) => `Cancelled release "${r.version}"` });

  const handleSetToRelease = (next) => setReleaseTagWithAlert(release.version, { to_release: next }, {
    reloadData,
    successTitle: 'To-release target',
    successMessage: (r) => (next ? `${r.release.version} set as to-release target` : `${r.release.version} removed as to-release target`),
    errorTitle: 'Set to release error',
  }).catch(() => {});

  const handleSetLatest = () => setReleaseTagWithAlert(release.version, { is_latest: true }, {
    reloadData,
    successTitle: 'Latest release',
    successMessage: (r) => `${r.release.version} is now the latest release`,
    errorTitle: 'Set latest error',
  }).catch(() => {});

  if (!release?.status) return null;

  const testingButtons = (
    <>
      <Btn variant="soft" size="sm" icon={ICO_PLAY} onClick={handleRunTest}>Re-run tests</Btn>
      {!release.to_release && (
        <Btn variant="soft" size="sm" icon={ICO_TAG} onClick={() => handleSetToRelease(true)}>Set as to-release target</Btn>
      )}
      <Btn variant="success" size="sm" icon={ICO_PLAY} onClick={handleRelease}>Release</Btn>
    </>
  );

  const buttons = {
    planned: (
      <>
        <Btn variant="success" size="sm" icon={ICO_BRANCH} onClick={handleBranch}>Create branches</Btn>
        <Btn variant="danger" size="sm" icon={ICO_TRASH} onClick={handleDelete}>Delete</Btn>
      </>
    ),
    cancelled: (
      <Btn variant="secondary" size="sm" icon={ICO_TRASH} onClick={handleDelete}>Delete</Btn>
    ),
    branched: (
      <>
        <Btn variant="success" size="sm" icon={ICO_PLAY} onClick={handleRunTest}>Run tests</Btn>
        <Btn variant="danger" size="sm" icon={ICO_XMARK} onClick={handleCancel}>Cancel</Btn>
      </>
    ),
    testing: testingButtons,
    validating: testingButtons,
    released: !release.is_latest ? (
      <Btn variant="soft" size="sm" icon={ICO_STAR} onClick={handleSetLatest}>Set as latest release</Btn>
    ) : null,
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {buttons[release.status] ?? null}
      </div>
      <ConfirmDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        confirmLabel={dialog.confirmLabel}
        danger={dialog.danger}
        onConfirm={dialog.onConfirm}
        onCancel={closeDialog}
      />
    </>
  );
}
